import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

type AsnTab = 'mapping' | 'rows' | 'json' | 'results';
type ThemeMode = 'dark' | 'light';
type AsnFileMode = 'active' | 'archive';
type SpencerAsnCustomerKind = 'spencer' | 'spirit' | 'mixed';

interface SpencerAsnRemoteFile {
  filename: string;
  size: number | null;
  modified_at: string | null;
  selected?: boolean;
  archived?: boolean;
}

interface SpencerAsnStorePreview {
  order_number: string;
  source_order_numbers?: string[];
  source_order_count?: number;
  combined?: boolean;
  store_number: string;
  store_bol: string;
  rows: number;
  total_cartons: number;
  total_weight: number;
  source_order_weights?: Record<string, number>;
  weight_mode?: 'single_order_total' | 'repeated_shipment_total' | 'summed_order_totals';
  lookup_status: string;
  product_actions?: {
    insert: number;
    update: number;
    empty_barcode: number;
  };
}

interface SpencerAsnMergePlan {
  enabled: boolean;
  original_order_groups: number;
  split_order_groups: number;
  resulting_store_groups: number;
  combined_store_groups: number;
  shipment_identity?: string;
}

interface SpencerAsnGroupSegmentPreview {
  customer_kind: Exclude<SpencerAsnCustomerKind, 'mixed'>;
  customer_name?: string;
  pool_point: string;
  warehouse: string;
  row_count: number;
  store_count: number;
  cartons: number;
  weight: number;
  merge_plan?: SpencerAsnMergePlan;
  mapped_trip?: Record<string, unknown>;
  stores?: SpencerAsnStorePreview[];
}

interface BlindAsnReconciliation {
  status: 'matched' | 'blocked';
  trip_list_uuid?: string;
  trip_number?: string;
  preview_token?: string;
  coverage_percent?: number;
  source_cartons?: number;
  matched_cartons?: number;
  missing_cartons?: number;
  outside_asn_cartons?: number;
  duplicate_excess_rows?: number;
  scanned_rows?: number;
  scanned_distinct_cartons?: number;
  missing?: Array<{ barcode: string; store_number: string }>;
  outside_asn?: Array<{ barcode: string; store_number: string; asn_number: string | null }>;
  duplicates?: Array<{ barcode: string; excess_rows: number; product_uuids: string[] }>;
  stores?: Array<{ store_number: string; store_bol: string; expected_cartons: number; matched_cartons: number; missing_cartons: number; action: string }>;
  changes?: Record<string, unknown>;
}

interface SpencerAsnGroupPreview {
  reconciliation?: BlindAsnReconciliation;
  master_bol_number: string;
  customer_kind?: SpencerAsnCustomerKind;
  customer_name?: string;
  status: string;
  message: string;
  warehouse_external_reference: string;
  row_count: number;
  store_count: number;
  cartons: number;
  weight: number;
  pool_point: string;
  pool_point_uuid?: string;
  pool_points?: string[];
  warehouse: string;
  warehouses?: string[];
  week_no: number;
  validation_errors: string[];
  warnings: string[];
  merge_plan?: SpencerAsnMergePlan;
  mapped_trip?: Record<string, unknown>;
  stores?: SpencerAsnStorePreview[];
  segments?: SpencerAsnGroupSegmentPreview[];
}

interface SpencerAsnSpiritSelection {
  requested_master_bols: string[];
  effective_master_bols: string[];
  selected_master_bols: string[];
  available_master_bols: string[];
  unselected_master_bols: string[];
  requested_pool_overrides?: Record<string, string>;
  effective_pool_overrides?: Record<string, string>;
  requested_allow_unmapped_spirit_stores?: string[];
  effective_allow_unmapped_spirit_stores?: string[];
  validation_errors?: string[];
  selected_count: number;
  unselected_count: number;
}

interface SpencerAsnSpiritGroupPreview {
  master_bol_number: string;
  pool_point?: string | null;
  pool_points?: string[];
  missing_pool_rows?: number;
  pool_override_required?: boolean;
  pool_override_reason?: 'missing' | 'customer_pool_mismatch' | null;
  eligible_pool_overrides?: string[];
  requested_pool_override?: string | null;
  effective_pool_point?: string | null;
  pool_override_applied?: boolean;
}

interface SpencerAsnFilterSummary {
  source_total_rows: number;
  remaining_rows: number;
  ignored_rows: number;
  ignored_missing_master_bol_rows: number;
  ignored_existing_master_bol_rows: number;
  ignored_existing_master_bols: string[];
  ignored_row_numbers?: Array<{ data_row: number; master_bol_number: string; reason: string }>;
  ignore_rows_without_master_bol: boolean;
  ignore_existing_master_bol: boolean;
}

interface SpencerAsnFilePreview {
  filename: string;
  status: string;
  message: string;
  headers?: string[];
  row_counts?: Record<string, number>;
  filter_summary?: SpencerAsnFilterSummary;
  spirit_selection?: SpencerAsnSpiritSelection;
  spirit_groups?: SpencerAsnSpiritGroupPreview[];
  groups: SpencerAsnGroupPreview[];
}

interface SpencerAsnListResponse {
  success: boolean;
  message: string;
  generated_at: string;
  config?: Record<string, unknown>;
  files: SpencerAsnRemoteFile[];
  archived_files?: SpencerAsnRemoteFile[];
}

interface SpencerAsnDownloadedFile {
  filename: string;
  content: string;
  bytes?: number;
  archived?: boolean;
}

interface SpencerAsnDownloadResponse {
  success: boolean;
  message: string;
  generated_at: string;
  files: SpencerAsnDownloadedFile[];
}

interface SpencerAsnParsedFile {
  filename: string;
  content: string;
  headers: string[];
  rows: Record<string, string>[];
  skipped_rows: number;
  row_count: number;
}

interface SpencerAsnPreviewResponse {
  success: boolean;
  message: string;
  generated_at: string;
  files: SpencerAsnFilePreview[];
}

interface SpencerAsnProcessMessage {
  filename: string | null;
  master_bol_number?: string;
  status: string;
  message: string;
  trip_number?: string | number;
  stores?: number;
  cartons?: number;
  weight?: number;
  processed_master_bols?: number;
  skipped_master_bols?: number;
  ignored_spirit_rows?: number;
  ignored_other_rows?: number;
  warehouse_external_reference?: string;
  customer_kind?: SpencerAsnCustomerKind;
}

interface SpencerAsnProcessResult {
  filename: string;
  status: string;
  filter_summary?: SpencerAsnFilterSummary;
  spirit_selection?: SpencerAsnSpiritSelection;
  messages: SpencerAsnProcessMessage[];
}

interface SpencerAsnProcessResponse {
  success: boolean;
  message: string;
  generated_at: string;
  results: SpencerAsnProcessResult[];
  messages: SpencerAsnProcessMessage[];
  archive_files?: string[];
}

interface SpencerAsnArchiveResponse {
  success: boolean;
  message: string;
  generated_at: string;
  results: Array<{ filename: string; status: string; message: string; archive_path?: string | null }>;
}

interface AsnIssue {
  filename: string;
  master_bol_number?: string;
  severity: 'error' | 'warning';
  message: string;
}

interface RawMasterBolSummary {
  master_bol_number: string;
  rows: number;
  spencer_rows: number;
  spirit_rows: number;
  other_rows: number;
}

interface SpiritMasterBolCandidate {
  master_bol_number: string;
  customer_kind: 'spirit' | 'mixed';
  required: boolean;
  spencer_row_count: number;
  spirit_row_count: number;
  mixed_pool_points: string[];
  source_pool: string;
  source_pool_points: string[];
  missing_pool_rows: number;
  pool_override_required: boolean;
  pool_override_reason: 'missing' | 'customer_pool_mismatch' | '';
  eligible_pool_overrides: string[];
  pool_override: string;
  preview_effective_pool: string;
  allow_unmapped_stores: boolean;
  preview_allow_unmapped_stores: boolean;
  warehouse: string;
  order_count: number;
  carton_count: number;
  selected: boolean;
}

interface SpiritCandidateFile {
  filename: string;
  candidates: SpiritMasterBolCandidate[];
}

type SpiritSelectionsByFilename = Record<string, string[]>;
type SpiritPoolOverridesByFilename = Record<string, Record<string, string>>;
type SpiritAllowUnmappedByFilename = Record<string, string[]>;

interface SpiritPoolOption {
  pool_point_client_id: string;
  pool_point_client_uuid: string;
  pool_point_uuid: string;
  pool_point_client_name: string | null;
  pool_point_client_scope: string | null;
  pool_point_name: string | null;
  same_pool_client_ids?: string[];
}

interface SpiritPoolOptionsResponse {
  success: boolean;
  message: string;
  pools: SpiritPoolOption[];
}

@Component({
  selector: 'app-spencer-asn-importer',
  templateUrl: './spencer-asn-importer.component.html',
  styleUrls: ['./spencer-asn-importer.component.scss']
})
export class SpencerAsnImporterComponent implements OnInit {
  files: SpencerAsnRemoteFile[] = [];
  archivedFiles: SpencerAsnRemoteFile[] = [];
  parsedFiles: SpencerAsnParsedFile[] = [];
  previewFiles: SpencerAsnFilePreview[] = [];
  reviewedReconciliationTokens = new Set<string>();
  processResults: SpencerAsnProcessResult[] = [];
  config: Record<string, unknown> | null = null;
  listGeneratedAt = '';
  previewGeneratedAt = '';
  processGeneratedAt = '';
  selectedPreviewFilename = '';
  selectedRawMasterBol = 'all';
  errorMessage = '';
  loadingFiles = false;
  previewing = false;
  downloading = false;
  archiving = false;
  processing = false;
  activeTab: AsnTab = 'mapping';
  fileMode: AsnFileMode = 'active';
  themeMode: ThemeMode = 'dark';
  combineSplitOrdersByStoreBol = true;
  ignoreRowsWithoutMasterBol = false;
  ignoreExistingMasterBol = false;
  spiritCandidateFiles: SpiritCandidateFile[] = [];
  spiritPoolOptions: SpiritPoolOption[] = [];
  loadingSpiritPoolOptions = false;

  private readonly apiUrl = '/api/asn_imports/spencer_asn.php';
  private readonly finalProcessPostEnabled = true;
  private readonly themeStorageKey = 'spencer-asn-importer-theme';
  private previewCombineSplitOrdersByStoreBol: boolean | null = null;
  private previewIgnoreRowsWithoutMasterBol: boolean | null = null;
  private previewIgnoreExistingMasterBol: boolean | null = null;
  private previewSpiritSelectionsByFilename: SpiritSelectionsByFilename | null = null;
  private previewSpiritPoolOverridesByFilename: SpiritPoolOverridesByFilename | null = null;
  private previewSpiritAllowUnmappedByFilename: SpiritAllowUnmappedByFilename | null = null;

  constructor(
    private http: HttpClient,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.initializeTheme();
    this.loadFiles();
    this.loadSpiritPoolOptions();
  }

  get selectedCount(): number {
    return this.visibleFiles.filter((file) => file.selected).length;
  }

  get allSelected(): boolean {
    return this.visibleFiles.length > 0 && this.selectedCount === this.visibleFiles.length;
  }

  get archiveCount(): number {
    return this.archivedFiles.length;
  }

  get visibleFiles(): SpencerAsnRemoteFile[] {
    return this.fileMode === 'archive' ? this.archivedFiles : this.files;
  }

  get visibleFileCount(): number {
    return this.visibleFiles.length;
  }

  get isArchiveMode(): boolean {
    return this.fileMode === 'archive';
  }

  get selectedPreview(): SpencerAsnFilePreview | null {
    if (!this.previewFiles.length) {
      return null;
    }

    return this.previewFiles.find((file) => file.filename === this.selectedPreviewFilename) ?? this.previewFiles[0];
  }

  get selectedParsedFile(): SpencerAsnParsedFile | null {
    if (!this.parsedFiles.length) {
      return null;
    }

    const filename = this.selectedPreview?.filename || this.selectedPreviewFilename;
    return this.parsedFiles.find((file) => file.filename === filename) ?? this.parsedFiles[0];
  }

  get selectedRowColumns(): string[] {
    return this.selectedParsedFile?.headers ?? [];
  }

  get rawMasterBolSummaries(): RawMasterBolSummary[] {
    const file = this.selectedParsedFile;
    if (!file) {
      return [];
    }

    const summaries = new Map<string, RawMasterBolSummary>();

    for (const row of file.rows) {
      const masterBol = this.rawMasterBolValue(row);
      const summary = summaries.get(masterBol) ?? {
        master_bol_number: masterBol,
        rows: 0,
        spencer_rows: 0,
        spirit_rows: 0,
        other_rows: 0
      };

      summary.rows++;

      const locationType = this.rawLocationType(row);
      if (locationType === 'spencer') {
        summary.spencer_rows++;
      } else if (locationType === 'spirit') {
        summary.spirit_rows++;
      } else {
        summary.other_rows++;
      }

      summaries.set(masterBol, summary);
    }

    return Array.from(summaries.values()).sort((a, b) => a.master_bol_number.localeCompare(b.master_bol_number));
  }

  get filteredRawRows(): Record<string, string>[] {
    const file = this.selectedParsedFile;
    if (!file) {
      return [];
    }

    if (this.selectedRawMasterBol === 'all') {
      return file.rows;
    }

    return file.rows.filter((row) => this.rawMasterBolValue(row) === this.selectedRawMasterBol);
  }

  get filteredRawRowCount(): number {
    return this.filteredRawRows.length;
  }

  get previewJson(): string {
    return JSON.stringify(this.selectedPreview ?? this.previewFiles, null, 2);
  }

  get processJson(): string {
    return JSON.stringify(this.processResults, null, 2);
  }

  get totalGroupCount(): number {
    return this.previewFiles.reduce((total, file) => total + file.groups.length, 0);
  }

  masterBolCountFor(filename: string): number | null {
    const parsed = this.parsedFiles.find((file) => file.filename === filename);
    if (parsed) {
      return this.uniqueMasterBols(parsed.rows).length;
    }

    const preview = this.previewFiles.find((file) => file.filename === filename);
    if (!preview) {
      return null;
    }

    return preview.row_counts?.['master_bol_groups'] ?? preview.groups.length;
  }

  get selectedRawMasterBolCount(): number {
    return this.selectedParsedFile ? this.uniqueMasterBols(this.selectedParsedFile.rows).length : 0;
  }

  get spiritCandidateCount(): number {
    return this.spiritCandidateFiles.reduce((total, file) => total + file.candidates.length, 0);
  }

  get selectedSpiritMasterBolCount(): number {
    return this.spiritCandidateFiles.reduce((total, file) => {
      return total + file.candidates.filter((candidate) => candidate.selected).length;
    }, 0);
  }

  get requiredMixedMasterBolCount(): number {
    return this.spiritCandidateFiles.reduce((total, file) => {
      return total + file.candidates.filter((candidate) => candidate.required).length;
    }, 0);
  }

  get selectedOptionalSpiritMasterBolCount(): number {
    return this.spiritCandidateFiles.reduce((total, file) => {
      return total + file.candidates.filter((candidate) => !candidate.required && candidate.selected).length;
    }, 0);
  }

  get unselectedOptionalSpiritMasterBolCount(): number {
    return this.spiritCandidateFiles.reduce((total, file) => {
      return total + file.candidates.filter((candidate) => !candidate.required && !candidate.selected).length;
    }, 0);
  }

  get unselectedSpiritMasterBolCount(): number {
    return this.spiritCandidateCount - this.selectedSpiritMasterBolCount;
  }

  get selectedSpiritPoolOverrideMissingCount(): number {
    return this.spiritCandidateFiles.reduce((total, file) => {
      return total + file.candidates.filter((candidate) => {
        return (candidate.required || candidate.selected)
          && candidate.pool_override_required
          && !candidate.pool_override;
      }).length;
    }, 0);
  }

  get selectedSpiritUnmappedAcknowledgementMissingCount(): number {
    return this.spiritCandidateFiles.reduce((total, file) => {
      return total + file.candidates.filter((candidate) => {
        return (candidate.required || candidate.selected)
          && this.isSpiritPoolCorrection(candidate)
          && !candidate.allow_unmapped_stores;
      }).length;
    }, 0);
  }

  get spiritPreviewChoiceMissingCount(): number {
    return this.selectedSpiritPoolOverrideMissingCount + this.selectedSpiritUnmappedAcknowledgementMissingCount;
  }

  get spiritSelectionBusy(): boolean {
    return this.loadingFiles || this.previewing || this.downloading || this.archiving || this.processing;
  }

  spiritSelectedCountFor(file: SpiritCandidateFile): number {
    return file.candidates.filter((candidate) => candidate.selected).length;
  }

  spiritOptionalCountFor(file: SpiritCandidateFile): number {
    return file.candidates.filter((candidate) => !candidate.required).length;
  }

  spiritAllSelected(file: SpiritCandidateFile): boolean {
    const optionalCandidates = file.candidates.filter((candidate) => !candidate.required);
    return optionalCandidates.length > 0 && optionalCandidates.every((candidate) => candidate.selected);
  }

  spiritSomeSelected(file: SpiritCandidateFile): boolean {
    const optionalCandidates = file.candidates.filter((candidate) => !candidate.required);
    const selected = optionalCandidates.filter((candidate) => candidate.selected).length;
    return selected > 0 && selected < optionalCandidates.length;
  }

  spiritPoolPickerVisible(candidate: SpiritMasterBolCandidate): boolean {
    return candidate.missing_pool_rows > 0 || candidate.eligible_pool_overrides.length > 0;
  }

  isSpiritPoolCorrection(candidate: SpiritMasterBolCandidate): boolean {
    const effectivePool = candidate.pool_override.trim();
    if (!effectivePool || !candidate.source_pool_points.length) {
      return false;
    }

    return candidate.pool_override_reason === 'customer_pool_mismatch'
      && candidate.source_pool_points.some((sourcePool) => sourcePool !== effectivePool);
  }

  spiritPoolCorrectionLabel(candidate: SpiritMasterBolCandidate): string {
    const source = candidate.source_pool_points.join(' + ') || candidate.source_pool;
    const effective = candidate.preview_effective_pool || candidate.pool_override;
    return effective ? `${source} → ${effective}` : source;
  }

  spiritPoolOptionsFor(candidate: SpiritMasterBolCandidate): SpiritPoolOption[] {
    if (!candidate.eligible_pool_overrides.length) {
      return this.spiritPoolOptions;
    }

    const eligible = new Set(candidate.eligible_pool_overrides);
    return this.spiritPoolOptions.filter((pool) => eligible.has(pool.pool_point_client_id));
  }

  masterBolCountLabel(filename: string): string {
    const count = this.masterBolCountFor(filename);
    if (count === null) {
      return '';
    }

    return `${count} Master BOL${count === 1 ? '' : 's'}`;
  }

  get readyGroupCount(): number {
    return this.previewFiles.reduce((total, file) => total + file.groups.filter((group) => group.status === 'ready').length, 0);
  }

  get blockedGroupCount(): number {
    return this.previewFiles.reduce((total, file) => total + file.groups.filter((group) => group.status === 'error').length, 0);
  }

  get blockedFileCount(): number {
    return this.previewFiles.filter((file) => {
      return file.status === 'error' && !file.groups.some((group) => group.status === 'error');
    }).length;
  }

  get blockingPreviewCount(): number {
    return this.blockedGroupCount + this.blockedFileCount;
  }

  get warningGroupCount(): number {
    return this.previewFiles.reduce((total, file) => total + file.groups.filter((group) => group.warnings.length > 0 && group.status !== 'error').length, 0);
  }

  get skippedGroupCount(): number {
    return this.previewFiles.reduce((total, file) => total + file.groups.filter((group) => group.status === 'skipped').length, 0);
  }

  get selectedFileBytes(): number {
    return this.visibleFiles
      .filter((file) => file.selected)
      .reduce((total, file) => total + (file.size ?? 0), 0);
  }

  get readinessPercent(): number {
    const previewCheckCount = this.totalGroupCount + this.blockedFileCount;
    if (!previewCheckCount) {
      return 0;
    }

    return Math.round((this.readyGroupCount / previewCheckCount) * 100);
  }

  get readinessLabel(): string {
    if (!this.previewFiles.length) {
      return 'Preview Needed';
    }

    if (!this.previewMatchesSelection) {
      return 'Preview Out Of Sync';
    }

    if (this.blockingPreviewCount > 0) {
      return 'Blocked';
    }

    if (this.readyGroupCount > 0 && this.warningGroupCount > 0) {
      return 'Review Warnings';
    }

    if (this.readyGroupCount > 0) {
      return 'Good To Go';
    }

    return 'Nothing To Process';
  }

  get readinessTone(): 'idle' | 'ready' | 'warning' | 'error' {
    if (!this.previewFiles.length) {
      return 'idle';
    }

    if (!this.previewMatchesSelection) {
      return 'warning';
    }

    if (this.blockingPreviewCount > 0) {
      return 'error';
    }

    if (this.warningGroupCount > 0) {
      return 'warning';
    }

    return this.readyGroupCount > 0 ? 'ready' : 'idle';
  }

  get issues(): AsnIssue[] {
    const issues: AsnIssue[] = [];

    for (const file of this.previewFiles) {
      if (!file.groups.length && file.status === 'error') {
        issues.push({ filename: file.filename, severity: 'error', message: file.message });
      }

      for (const group of file.groups) {
        for (const message of group.validation_errors ?? []) {
          issues.push({ filename: file.filename, master_bol_number: group.master_bol_number, severity: 'error', message });
        }

        for (const message of group.warnings ?? []) {
          issues.push({ filename: file.filename, master_bol_number: group.master_bol_number, severity: 'warning', message });
        }
      }
    }

    return issues;
  }

  get visibleIssues(): AsnIssue[] {
    return this.issues.slice(0, 8);
  }

  get previewMatchesSelection(): boolean {
    if (!this.previewFiles.length || !this.selectedCount) {
      return false;
    }

    if (this.previewCombineSplitOrdersByStoreBol === null
      || this.previewCombineSplitOrdersByStoreBol !== this.combineSplitOrdersByStoreBol
      || this.previewIgnoreRowsWithoutMasterBol === null
      || this.previewIgnoreRowsWithoutMasterBol !== this.ignoreRowsWithoutMasterBol
      || this.previewIgnoreExistingMasterBol === null
      || this.previewIgnoreExistingMasterBol !== this.ignoreExistingMasterBol) {
      return false;
    }

    if (this.previewSpiritSelectionsByFilename === null
      || !this.spiritSelectionMapsEqual(
        this.currentSpiritSelectionsByFilename(),
        this.previewSpiritSelectionsByFilename
      )) {
      return false;
    }

    if (this.previewSpiritPoolOverridesByFilename === null
      || !this.spiritPoolOverrideMapsEqual(
        this.currentSpiritPoolOverridesByFilename(),
        this.previewSpiritPoolOverridesByFilename
      )) {
      return false;
    }

    if (this.previewSpiritAllowUnmappedByFilename === null
      || !this.spiritAllowUnmappedMapsEqual(
        this.currentSpiritAllowUnmappedByFilename(),
        this.previewSpiritAllowUnmappedByFilename
      )) {
      return false;
    }

    const selected = this.selectedFilenames().slice().sort();
    const previewed = this.previewFiles.map((file) => file.filename).sort();

    if (selected.length !== previewed.length) {
      return false;
    }

    return selected.every((filename, index) => filename === previewed[index]);
  }

  get previewOptionChanged(): boolean {
    return this.previewFiles.length > 0
      && (this.previewCombineSplitOrdersByStoreBol === null
        || this.previewCombineSplitOrdersByStoreBol !== this.combineSplitOrdersByStoreBol
        || this.previewIgnoreRowsWithoutMasterBol === null
        || this.previewIgnoreRowsWithoutMasterBol !== this.ignoreRowsWithoutMasterBol
        || this.previewIgnoreExistingMasterBol === null
        || this.previewIgnoreExistingMasterBol !== this.ignoreExistingMasterBol);
  }

  get previewSpiritSelectionChanged(): boolean {
    return this.previewFiles.length > 0
      && (this.previewSpiritSelectionsByFilename === null
        || !this.spiritSelectionMapsEqual(
          this.currentSpiritSelectionsByFilename(),
          this.previewSpiritSelectionsByFilename
        ));
  }

  get previewSpiritPoolOverrideChanged(): boolean {
    return this.previewFiles.length > 0
      && (this.previewSpiritPoolOverridesByFilename === null
        || !this.spiritPoolOverrideMapsEqual(
          this.currentSpiritPoolOverridesByFilename(),
          this.previewSpiritPoolOverridesByFilename
        ));
  }

  get previewSpiritAllowUnmappedChanged(): boolean {
    return this.previewFiles.length > 0
      && (this.previewSpiritAllowUnmappedByFilename === null
        || !this.spiritAllowUnmappedMapsEqual(
          this.currentSpiritAllowUnmappedByFilename(),
          this.previewSpiritAllowUnmappedByFilename
        ));
  }

  get previewOutOfSyncMessage(): string {
    if (this.previewOptionChanged) {
      return 'An import option changed after preview.';
    }

    if (this.previewSpiritSelectionChanged) {
      return 'The Spirit Master BOL selection changed or was not acknowledged by the backend.';
    }

    if (this.previewSpiritPoolOverrideChanged) {
      return 'A Spirit replenishment pool choice changed or was not acknowledged by the backend.';
    }

    if (this.previewSpiritAllowUnmappedChanged) {
      return 'The held/out-of-market carton acknowledgement changed or was not acknowledged by the backend.';
    }

    return 'The selected files changed after preview.';
  }

  get combinedStoreBolGroupCount(): number {
    return this.previewFiles.reduce((fileTotal, file) => {
      return fileTotal + file.groups.reduce((groupTotal, group) => {
        if (group.status !== 'ready') {
          return groupTotal;
        }

        if (group.merge_plan) {
          return groupTotal + (group.merge_plan.combined_store_groups ?? 0);
        }

        return groupTotal + (group.segments ?? []).reduce((segmentTotal, segment) => {
          return segmentTotal + (segment.merge_plan?.combined_store_groups ?? 0);
        }, 0);
      }, 0);
    }, 0);
  }

  get blindReconciliationGroups(): SpencerAsnGroupPreview[] {
    return this.previewFiles.flatMap((file) => file.groups)
      .filter((group) => group.reconciliation?.status === 'matched');
  }

  get unreviewedReconciliationCount(): number {
    return this.blindReconciliationGroups.filter((group) => !this.isReconciliationReviewed(group)).length;
  }

  isReconciliationReviewed(group: SpencerAsnGroupPreview): boolean {
    return !!group.reconciliation?.preview_token
      && this.reviewedReconciliationTokens.has(group.reconciliation.preview_token);
  }

  reviewReconciliation(group: SpencerAsnGroupPreview, reviewed: boolean): void {
    const token = group.reconciliation?.preview_token;
    if (!token) return;
    if (reviewed) this.reviewedReconciliationTokens.add(token);
    else this.reviewedReconciliationTokens.delete(token);
  }

  get canProcessPreview(): boolean {
    return !this.isArchiveMode
      && this.selectedCount > 0
      && this.previewFiles.length > 0
      && this.previewMatchesSelection
      && this.blockingPreviewCount === 0
      && this.unreviewedReconciliationCount === 0
      && this.readyGroupCount > 0;
  }

  get canRunFinalProcess(): boolean {
    return this.finalProcessPostEnabled && this.canProcessPreview;
  }

  get canArchiveSelected(): boolean {
    return !this.isArchiveMode && this.selectedCount > 0;
  }

  get archiveButtonTitle(): string {
    if (this.isArchiveMode) {
      return 'Archived files are already in the archive';
    }

    if (!this.selectedCount) {
      return 'Select active files to archive';
    }

    return 'Move selected active CSV files to archive';
  }

  get previewButtonTitle(): string {
    if (this.selectedSpiritPoolOverrideMissingCount > 0) {
      return 'Choose a replenishment pool for each selected Spirit Master BOL that requires correction';
    }

    if (this.selectedSpiritUnmappedAcknowledgementMissingCount > 0) {
      return 'Acknowledge the held/out-of-market cartons for each corrected Spirit Master BOL';
    }

    return this.isArchiveMode ? 'Preview selected archived files' : 'Preview selected files';
  }

  get processButtonLabel(): string {
    if (this.processing) {
      return 'Processing';
    }

    return this.finalProcessPostEnabled ? 'Process' : 'Process Disabled';
  }

  get processButtonIcon(): string {
    if (this.processing) {
      return 'fa-solid fa-play';
    }

    return this.finalProcessPostEnabled ? 'fa-solid fa-play' : 'fa-solid fa-lock';
  }

  get processDisabledReason(): string {
    if (this.unreviewedReconciliationCount > 0) return 'Review and acknowledge each blind-trip reconciliation in Mapping before processing.';
    if (this.isArchiveMode) {
      return 'Switch to active files to process';
    }

    if (!this.selectedCount) {
      return 'Select files first';
    }

    if (!this.previewFiles.length) {
      return 'Preview selected files first';
    }

    if (!this.previewMatchesSelection) {
      if (this.previewOptionChanged) {
        return 'Import filter option changed; run Preview again';
      }

      if (this.previewSpiritSelectionChanged) {
        return 'Spirit Master BOL selection changed or was not acknowledged; run Preview again';
      }

      if (this.previewSpiritPoolOverrideChanged) {
        return 'Spirit pool choice changed or was not acknowledged; run Preview again';
      }

      if (this.previewSpiritAllowUnmappedChanged) {
        return 'Held/out-of-market carton choice changed or was not acknowledged; run Preview again';
      }

      return 'Preview is out of sync';
    }

    if (this.blockingPreviewCount > 0) {
      return 'Resolve blocked files or groups';
    }

    if (!this.readyGroupCount) {
      return 'No ready groups';
    }

    return this.finalProcessPostEnabled ? 'Ready to process' : 'Ready to process; final import is disabled';
  }

  loadSpiritPoolOptions(): void {
    this.loadingSpiritPoolOptions = true;
    this.http.post<SpiritPoolOptionsResponse>(this.apiUrl, { action: 'pool_options' }).subscribe({
      next: (response) => {
        this.spiritPoolOptions = response.success ? (response.pools ?? []) : [];
        this.applyImmediateSpiritPoolEligibility();
        this.loadingSpiritPoolOptions = false;
      },
      error: () => {
        this.spiritPoolOptions = [];
        this.loadingSpiritPoolOptions = false;
      }
    });
  }

  loadFiles(): void {
    this.loadingFiles = true;
    this.errorMessage = '';

    this.http.get<SpencerAsnListResponse>(`${this.apiUrl}?action=list`).subscribe({
      next: (response) => {
        this.files = (response.files ?? []).map((file) => ({ ...file, selected: false, archived: false }));
        this.archivedFiles = (response.archived_files ?? []).map((file) => ({ ...file, selected: false, archived: true }));
        this.config = response.config ?? null;
        this.listGeneratedAt = response.generated_at;
        this.parsedFiles = [];
        this.previewFiles = [];
        this.spiritCandidateFiles = [];
        this.previewCombineSplitOrdersByStoreBol = null;
        this.previewSpiritSelectionsByFilename = null;
        this.previewSpiritPoolOverridesByFilename = null;
        this.previewSpiritAllowUnmappedByFilename = null;
        this.selectedPreviewFilename = '';
        this.selectedRawMasterBol = 'all';
        this.loadingFiles = false;
      },
      error: (error) => {
        this.errorMessage = this.errorText(error, 'Unable to load Spencer ASN files.');
        this.loadingFiles = false;
      }
    });
  }

  toggleAll(checked: boolean): void {
    if (this.isArchiveMode) {
      this.archivedFiles = this.archivedFiles.map((file) => ({ ...file, selected: checked }));
      return;
    }

    this.files = this.files.map((file) => ({ ...file, selected: checked }));
  }

  onSpiritSelectionChanged(): void {
    // Even if the user changes a checkbox back, require a fresh backend
    // acknowledgement for the exact set before Process can be enabled again.
    this.previewSpiritSelectionsByFilename = null;
    this.previewSpiritPoolOverridesByFilename = null;
    this.previewSpiritAllowUnmappedByFilename = null;
    this.clearSpiritPoolAcknowledgements();
  }

  onSpiritPoolOverrideChanged(candidate: SpiritMasterBolCandidate): void {
    candidate.allow_unmapped_stores = false;
    this.previewSpiritPoolOverridesByFilename = null;
    this.previewSpiritAllowUnmappedByFilename = null;
    this.clearSpiritPoolAcknowledgements();
  }

  onSpiritUnmappedAcknowledgementChanged(): void {
    this.previewSpiritAllowUnmappedByFilename = null;
    this.clearSpiritPoolAcknowledgements();
  }

  toggleSpiritCandidatesForFile(file: SpiritCandidateFile, checked: boolean): void {
    for (const candidate of file.candidates) {
      candidate.selected = candidate.required || checked;
    }

    this.onSpiritSelectionChanged();
  }

  setFileMode(mode: AsnFileMode): void {
    this.fileMode = mode;
  }

  previewSelected(): void {
    const filenames = this.selectedFilenames();

    if (!filenames.length) {
      this.toastr.warning('Select at least one CSV file.');
      return;
    }

    if (this.selectedSpiritPoolOverrideMissingCount > 0) {
      this.toastr.warning('Choose a replenishment pool for every selected Spirit Master BOL that requires a correction.');
      return;
    }

    if (this.selectedSpiritUnmappedAcknowledgementMissingCount > 0) {
      this.toastr.warning('Acknowledge the held/out-of-market cartons for every corrected Spirit Master BOL.');
      return;
    }

    this.previewing = true;
    this.reviewedReconciliationTokens.clear();
    this.errorMessage = '';
    this.activeTab = 'mapping';
    const combineSplitOrdersByStoreBol = this.combineSplitOrdersByStoreBol;

    // Invalidate the previous preview before replacing its parsed source rows.
    // A failed refresh must never leave Process enabled against stale validation.
    this.previewCombineSplitOrdersByStoreBol = null;
    this.previewIgnoreRowsWithoutMasterBol = null;
    this.previewIgnoreExistingMasterBol = null;
    this.previewSpiritSelectionsByFilename = null;
    this.previewSpiritPoolOverridesByFilename = null;
    this.previewSpiritAllowUnmappedByFilename = null;
    this.clearSpiritPoolAcknowledgements();

    this.http.post<SpencerAsnDownloadResponse>(this.apiUrl, {
      action: 'download',
      filenames,
      archived: this.isArchiveMode
    }).subscribe({
      next: (downloadResponse) => {
        let parsedFiles: SpencerAsnParsedFile[];

        try {
          parsedFiles = (downloadResponse.files ?? []).map((file) => this.parseDownloadedFile(file));
        } catch (error) {
          this.errorMessage = error instanceof Error ? error.message : 'Unable to parse selected ASN files.';
          this.previewing = false;
          return;
        }

        this.parsedFiles = parsedFiles;
        this.reconcileSpiritCandidates(parsedFiles);
        const requestedSpiritSelections = this.currentSpiritSelectionsByFilename(filenames);
        const requestedSpiritPoolOverrides = this.currentSpiritPoolOverridesByFilename(filenames);
        const requestedSpiritAllowUnmapped = this.currentSpiritAllowUnmappedByFilename(filenames);

        this.http.post<SpencerAsnPreviewResponse>(this.apiUrl, {
          action: 'preview',
          files: this.pluginFilePayload(
            parsedFiles,
            requestedSpiritSelections,
            requestedSpiritPoolOverrides,
            requestedSpiritAllowUnmapped
          ),
          options: this.pluginOptions(
            combineSplitOrdersByStoreBol,
            this.ignoreRowsWithoutMasterBol,
            this.ignoreExistingMasterBol
          )
        }).subscribe({
          next: (response) => {
            this.previewFiles = response.files ?? [];
            this.applySpiritCandidateMetadata(this.previewFiles);
            this.previewGeneratedAt = response.generated_at;
            this.selectedPreviewFilename = this.previewFiles[0]?.filename ?? '';
            this.selectedRawMasterBol = 'all';
            this.previewing = false;

            if (!response.success) {
              this.errorMessage = response.message || 'Backend preview failed.';
              this.toastr.error(this.errorMessage);
              return;
            }

            if (!this.previewAcknowledgesCombineOption(this.previewFiles, combineSplitOrdersByStoreBol)) {
              this.errorMessage = 'Backend preview did not acknowledge the selected split-order option. Processing remains disabled.';
              this.toastr.error(this.errorMessage);
              return;
            }

            if (!this.filesAcknowledgeSpiritSelection(
              this.previewFiles,
              requestedSpiritSelections,
              requestedSpiritPoolOverrides,
              requestedSpiritAllowUnmapped
            )) {
              this.errorMessage = 'Backend preview did not acknowledge the exact Spirit Master BOL selection, pool choices, and held/out-of-market carton choices. Processing remains disabled.';
              this.toastr.error(this.errorMessage);
              return;
            }

            this.previewCombineSplitOrdersByStoreBol = combineSplitOrdersByStoreBol;
            this.previewIgnoreRowsWithoutMasterBol = this.ignoreRowsWithoutMasterBol;
            this.previewIgnoreExistingMasterBol = this.ignoreExistingMasterBol;
            this.previewSpiritSelectionsByFilename = this.cloneSpiritSelections(requestedSpiritSelections);
            this.previewSpiritPoolOverridesByFilename = this.cloneSpiritPoolOverrides(requestedSpiritPoolOverrides);
            this.previewSpiritAllowUnmappedByFilename = this.cloneSpiritAllowUnmapped(requestedSpiritAllowUnmapped);
            this.applySpiritPoolAcknowledgements(this.previewFiles);
            this.toastr.success('Preview generated.');
          },
          error: (error) => {
            this.errorMessage = this.errorText(error, 'Unable to generate preview.');
            this.previewing = false;
          }
        });
      },
      error: (error) => {
        this.errorMessage = this.errorText(error, 'Unable to download selected ASN files.');
        this.previewing = false;
      }
    });
  }

  downloadSelectedFiles(): void {
    const filenames = this.selectedFilenames();

    if (!filenames.length) {
      this.toastr.warning('Select at least one CSV file.');
      return;
    }

    this.downloading = true;
    this.errorMessage = '';

    this.http.post<SpencerAsnDownloadResponse>(this.apiUrl, {
      action: 'download',
      filenames,
      archived: this.isArchiveMode
    }).subscribe({
      next: (response) => {
        for (const file of response.files ?? []) {
          this.saveCsvFile(file);
        }

        this.downloading = false;
        this.toastr.success('Download started.');
      },
      error: (error) => {
        this.errorMessage = this.errorText(error, 'Unable to download selected CSV files.');
        this.downloading = false;
      }
    });
  }

  archiveSelectedFiles(): void {
    if (this.isArchiveMode) {
      this.toastr.info('Archived files are already in the archive.');
      return;
    }

    const filenames = this.selectedFilenames();

    if (!filenames.length) {
      this.toastr.warning('Select at least one active CSV file.');
      return;
    }

    const confirmed = window.confirm(`Archive ${filenames.length} selected ASN file${filenames.length === 1 ? '' : 's'} without processing?`);
    if (!confirmed) {
      return;
    }

    this.archiving = true;
    this.errorMessage = '';

    this.http.post<SpencerAsnArchiveResponse>(this.apiUrl, {
      action: 'archive',
      filenames
    }).subscribe({
      next: (response) => {
        this.archiving = false;
        if (response.success) {
          this.toastr.success('Selected ASN files archived.');
        } else {
          this.toastr.warning('One or more selected files did not archive.');
        }
        this.loadFiles();
      },
      error: (error) => {
        this.errorMessage = this.errorText(error, 'Unable to archive selected files.');
        this.archiving = false;
        this.loadFiles();
      }
    });
  }

  processSelected(): void {
    if (!this.finalProcessPostEnabled) {
      this.toastr.info('Final import is disabled. Preview is available for readiness checks.');
      return;
    }

    const filenames = this.selectedFilenames();

    if (!filenames.length) {
      this.toastr.warning('Select at least one CSV file.');
      return;
    }

    if (!this.canProcessPreview) {
      this.toastr.warning(this.processDisabledReason);
      return;
    }

    const combineSplitOrdersByStoreBol = this.previewCombineSplitOrdersByStoreBol;
    if (combineSplitOrdersByStoreBol === null) {
      this.toastr.warning('Preview is out of sync. Run preview again.');
      return;
    }

    const spiritSelections = this.previewSpiritSelectionsByFilename;
    if (spiritSelections === null
      || !this.spiritSelectionMapsEqual(this.currentSpiritSelectionsByFilename(filenames), spiritSelections)) {
      this.toastr.warning('Spirit Master BOL selection is out of sync. Run preview again.');
      return;
    }

    const spiritPoolOverrides = this.previewSpiritPoolOverridesByFilename;
    if (spiritPoolOverrides === null
      || !this.spiritPoolOverrideMapsEqual(this.currentSpiritPoolOverridesByFilename(filenames), spiritPoolOverrides)) {
      this.toastr.warning('Spirit pool choices are out of sync. Run preview again.');
      return;
    }

    const spiritAllowUnmapped = this.previewSpiritAllowUnmappedByFilename;
    if (spiritAllowUnmapped === null
      || !this.spiritAllowUnmappedMapsEqual(
        this.currentSpiritAllowUnmappedByFilename(filenames),
        spiritAllowUnmapped
      )) {
      this.toastr.warning('Held/out-of-market carton choices are out of sync. Run preview again.');
      return;
    }

    const parsedFiles = this.parsedFilesFor(filenames);
    if (parsedFiles.length !== filenames.length) {
      this.toastr.warning('Preview is out of sync. Run preview again.');
      return;
    }

    const combinedStoreBolGroupCount = this.combinedStoreBolGroupCount;
    const mergeConfirmation = combineSplitOrdersByStoreBol && combinedStoreBolGroupCount > 0
      ? `\n\nThe backend preview approved ${combinedStoreBolGroupCount} split store/BOL group${combinedStoreBolGroupCount === 1 ? '' : 's'} to become one shipment each. It does not synthesize barcode values; it carries each parsed, validated barcode value with its carton row.`
      : '';
    const requiredMixedCount = this.requiredMixedMasterBolCount;
    const selectedStandaloneSpiritCount = this.selectedOptionalSpiritMasterBolCount;
    const unselectedStandaloneSpiritCount = this.unselectedOptionalSpiritMasterBolCount;
    const mixedConfirmation = requiredMixedCount > 0
      ? `${requiredMixedCount} mixed Spencer + Spirit Master BOL${requiredMixedCount === 1 ? '' : 's'} will be imported atomically as one trip each; neither customer can be processed alone. `
      : '';
    const spiritConfirmation = `\n\n${mixedConfirmation}Standalone Spirit selection: ${selectedStandaloneSpiritCount} selected; ${unselectedStandaloneSpiritCount} unselected. Unselected standalone Spirit rows will be ignored. After successful processing, each whole CSV is archived, so ignored rows will not remain active for a later run.`;
    const poolChoiceCount = Object.values(spiritPoolOverrides)
      .reduce((total, overrides) => total + Object.keys(overrides).length, 0);
    const poolConfirmation = poolChoiceCount > 0
      ? `\n\n${poolChoiceCount} selected Spirit Master BOL${poolChoiceCount === 1 ? '' : 's'} will use the exact replenishment pool corrections acknowledged by Preview. The CSV and barcode values are not changed.`
      : '';
    const allowUnmappedCount = Object.values(spiritAllowUnmapped)
      .reduce((total, masterBols) => total + masterBols.length, 0);
    const outOfMarketConfirmation = allowUnmappedCount > 0
      ? `\n\n${allowUnmappedCount} corrected Spirit Master BOL${allowUnmappedCount === 1 ? '' : 's'} may contain stores outside the selected pool. Those cartons remain on the ASN with their original store numbers and barcodes and may appear to scanners as OUT OF MARKET / HELD FOR DISPOSITION.`
      : '';
    const blindConfirmation = this.blindReconciliationGroups.map((group) => {
      const plan = group.reconciliation!;
      return `\n\nReconcile ASN ${group.master_bol_number} with existing blind trip ${plan.trip_number}: ${plan.matched_cartons} matched labels, ${plan.missing_cartons} new unreceived expectations, ${plan.outside_asn_cartons} outside-ASN rows and ${plan.duplicate_excess_rows} repeated rows preserved for review. Existing pallets and scan state are retained.`;
    }).join('');
    const confirmed = window.confirm(`Process ${filenames.length} selected ASN file${filenames.length === 1 ? '' : 's'}?${blindConfirmation}${mergeConfirmation}${spiritConfirmation}${poolConfirmation}${outOfMarketConfirmation}`);
    if (!confirmed) {
      return;
    }

    this.processing = true;
    this.errorMessage = '';
    this.activeTab = 'results';

    this.http.post<SpencerAsnProcessResponse>(this.apiUrl, {
      action: 'process',
      files: this.pluginFilePayload(parsedFiles, spiritSelections, spiritPoolOverrides, spiritAllowUnmapped),
      options: this.pluginOptions(
        combineSplitOrdersByStoreBol,
        this.ignoreRowsWithoutMasterBol,
        this.ignoreExistingMasterBol
      )
    }).subscribe({
      next: (response) => {
        this.processResults = response.results ?? [];
        this.processGeneratedAt = response.generated_at;

        const processComplete = this.processResponseSucceeded(
          response,
          filenames,
          spiritSelections,
          spiritPoolOverrides,
          spiritAllowUnmapped
        );
        const failureMessage = processComplete
          ? ''
          : this.processFailureMessage(response, filenames, spiritSelections, spiritPoolOverrides, spiritAllowUnmapped);
        const archiveFiles = this.archiveFilesFromProcess(
          response,
          filenames,
          spiritSelections,
          spiritPoolOverrides,
          spiritAllowUnmapped
        );
        if (archiveFiles.length) {
          this.archiveProcessedFiles(archiveFiles, processComplete, filenames.length, failureMessage);
          return;
        }

        this.processing = false;

        if (!processComplete) {
          this.errorMessage = failureMessage;
          this.toastr.error(failureMessage);
          return;
        }

        this.toastr.success('Selected ASN files processed.');
        this.loadFiles();
      },
      error: (error) => {
        this.errorMessage = this.errorText(error, 'Unable to process selected files.');
        this.processing = false;
      }
    });
  }

  selectPreviewFile(filename: string): void {
    this.selectedPreviewFilename = filename;
    this.selectedRawMasterBol = 'all';
    this.activeTab = 'mapping';
  }

  selectRawMasterBol(masterBol: string): void {
    this.selectedRawMasterBol = masterBol;
  }

  viewRawMasterBol(masterBol: string): void {
    this.selectedRawMasterBol = masterBol;
    this.activeTab = 'rows';
  }

  setActiveTab(tab: AsnTab): void {
    this.activeTab = tab;
  }

  toggleTheme(): void {
    this.themeMode = this.themeMode === 'dark' ? 'light' : 'dark';
    window.localStorage.setItem(this.themeStorageKey, this.themeMode);
  }

  getThemeIcon(): string {
    return this.themeMode === 'dark' ? 'fa-solid fa-sun' : 'fa-solid fa-moon';
  }

  getThemeLabel(): string {
    return this.themeMode === 'dark' ? 'Light' : 'Dark';
  }

  trackByFilename(_: number, file: { filename: string }): string {
    return file.filename;
  }

  trackByMasterBol(_: number, group: SpencerAsnGroupPreview): string {
    return `${group.master_bol_number}-${group.customer_kind ?? 'spencer'}`;
  }

  trackByStore(_: number, store: SpencerAsnStorePreview): string {
    return `${store.order_number}-${store.store_number}-${store.store_bol}`;
  }

  trackByMessage(index: number, message: SpencerAsnProcessMessage): string {
    return `${message.filename}-${message.master_bol_number ?? index}-${message.message}`;
  }

  trackByIssue(index: number, issue: AsnIssue): string {
    return `${issue.filename}-${issue.master_bol_number ?? 'file'}-${issue.message}-${index}`;
  }

  trackByRawMasterBol(_: number, summary: RawMasterBolSummary): string {
    return summary.master_bol_number;
  }

  trackBySpiritMasterBol(_: number, candidate: SpiritMasterBolCandidate): string {
    return candidate.master_bol_number;
  }

  trackBySegment(_: number, segment: SpencerAsnGroupSegmentPreview): string {
    return segment.customer_kind;
  }

  mixedCandidatePoolLabel(candidate: SpiritMasterBolCandidate): string {
    return candidate.mixed_pool_points.length
      ? candidate.mixed_pool_points.join(' + ')
      : 'Spencer + Spirit';
  }

  isMixedGroup(group: SpencerAsnGroupPreview): boolean {
    return group.customer_kind === 'mixed' || (group.segments?.length ?? 0) > 1;
  }

  groupCustomerLabel(group: SpencerAsnGroupPreview): string {
    if (this.isMixedGroup(group)) {
      return `Mixed — ${this.groupPoolLabel(group)}`;
    }

    return group.customer_name || this.customerKindLabel(group.customer_kind);
  }

  customerKindLabel(customerKind: SpencerAsnCustomerKind | undefined): string {
    if (customerKind === 'spirit') {
      return 'Spirit of Halloween';
    }

    if (customerKind === 'mixed') {
      return 'Spencer Gifts + Spirit of Halloween';
    }

    return 'Spencer Gifts';
  }

  groupPoolLabel(group: SpencerAsnGroupPreview): string {
    const poolPoints = this.normalizeStringArray(group.pool_points ?? []);
    return poolPoints.length ? poolPoints.join(' + ') : (group.pool_point || 'n/a');
  }

  groupWarehouseLabel(group: SpencerAsnGroupPreview): string {
    const warehouses = this.normalizeStringArray(group.warehouses ?? []);
    return warehouses.length ? warehouses.join(' + ') : (group.warehouse || 'n/a');
  }

  statusClass(status: string | undefined): string {
    const normalized = (status || 'unknown').toLowerCase().replace(/[^a-z0-9_-]/g, '-');
    return `asn-status--${normalized}`;
  }

  readinessClass(): string {
    return `asn-readiness--${this.readinessTone}`;
  }

  issueClass(issue: AsnIssue): string {
    return issue.severity === 'error' ? 'asn-issue--error' : 'asn-issue--warning';
  }

  formatBytes(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return 'n/a';
    }

    if (value < 1024) {
      return `${value} B`;
    }

    if (value < 1024 * 1024) {
      return `${(value / 1024).toFixed(1)} KB`;
    }

    return `${(value / 1024 / 1024).toFixed(1)} MB`;
  }

  formatDate(value: string | null | undefined): string {
    if (!value) {
      return 'n/a';
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  }

  objectKeys(value: Record<string, number> | undefined): string[] {
    return value ? Object.keys(value) : [];
  }

  labelize(value: string): string {
    return value.replace(/_/g, ' ');
  }

  sourceOrdersLabel(store: SpencerAsnStorePreview): string {
    const sourceOrders = (store.source_order_numbers ?? []).filter((orderNumber) => orderNumber !== '');
    return sourceOrders.length ? sourceOrders.join(', ') : store.order_number;
  }


  rawCellValue(row: Record<string, string>, column: string): string {
    return row[column] ?? '';
  }

  rawMasterBolClass(summary: RawMasterBolSummary): string {
    if (summary.spencer_rows > 0 && summary.spirit_rows > 0) {
      return 'is-mixed';
    }

    if (summary.spencer_rows > 0) {
      return 'is-importable';
    }

    if (summary.spirit_rows > 0 && summary.other_rows === 0) {
      return 'is-ignored';
    }

    return 'is-other';
  }

  rawMasterBolTitle(summary: RawMasterBolSummary): string {
    const classification = summary.spencer_rows > 0 && summary.spirit_rows > 0
      ? 'Mixed load — imported atomically'
      : (summary.spirit_rows > 0 && summary.spencer_rows === 0 ? 'Standalone Spirit selection' : 'Spencer load');
    return `${classification} / ${summary.rows} rows / ${summary.spencer_rows} Spencer / ${summary.spirit_rows} Spirit / ${summary.other_rows} Other`;
  }

  downloadParsedFile(file: SpencerAsnParsedFile): void {
    this.saveCsvFile({ filename: file.filename, content: file.content });
  }

  private parsedFilesFor(filenames: string[]): SpencerAsnParsedFile[] {
    const requested = new Set(filenames);
    return this.parsedFiles.filter((file) => requested.has(file.filename));
  }

  private pluginFilePayload(
    files: SpencerAsnParsedFile[],
    spiritSelections: SpiritSelectionsByFilename,
    spiritPoolOverrides: SpiritPoolOverridesByFilename,
    spiritAllowUnmapped: SpiritAllowUnmappedByFilename
  ): Array<Record<string, unknown>> {
    return files.map((file) => ({
      filename: file.filename,
      headers: file.headers,
      rows: file.rows,
      skipped_rows: file.skipped_rows,
      row_count: file.row_count,
      options: {
        blind_reconciliation_previews: Object.fromEntries(
          (this.previewFiles.find((preview) => preview.filename === file.filename)?.groups ?? [])
            .filter((group) => group.reconciliation?.status === 'matched' && this.isReconciliationReviewed(group))
            .map((group) => [
              `${group.customer_kind ?? 'spencer'}|${group.master_bol_number}`,
              { trip_list_uuid: group.reconciliation!.trip_list_uuid, preview_token: group.reconciliation!.preview_token }
            ])
        ),
        selected_spirit_master_bols: this.normalizeStringArray(spiritSelections[file.filename] ?? []),
        spirit_pool_overrides: this.normalizePoolOverrideMap(spiritPoolOverrides[file.filename] ?? {}),
        allow_unmapped_spirit_stores: this.normalizeStringArray(spiritAllowUnmapped[file.filename] ?? [])
      }
    }));
  }

  private pluginOptions(
    combineSplitOrdersByStoreBol: boolean,
    ignoreRowsWithoutMasterBol: boolean,
    ignoreExistingMasterBol: boolean
  ): Record<string, boolean> {
    return {
      make_ladings: false,
      combine_split_orders_by_store_bol: combineSplitOrdersByStoreBol,
      ignore_rows_without_master_bol: ignoreRowsWithoutMasterBol,
      ignore_existing_master_bol: ignoreExistingMasterBol
    };
  }

  private previewAcknowledgesCombineOption(files: SpencerAsnFilePreview[], expected: boolean): boolean {
    for (const file of files) {
      for (const group of file.groups ?? []) {
        if (group.merge_plan?.enabled === expected) {
          continue;
        }

        const segments = group.segments ?? [];
        if (!this.isMixedGroup(group)
          || segments.length < 2
          || segments.some((segment) => segment.merge_plan?.enabled !== expected)) {
          return false;
        }
      }
    }

    return true;
  }

  private reconcileSpiritCandidates(files: SpencerAsnParsedFile[]): void {
    const previouslySelected = new Set<string>();
    const previousPoolOverrides = new Map<string, string>();
    const previousAllowUnmapped = new Set<string>();
    const previousPoolMetadata = new Map<string, {
      required: boolean;
      reason: 'missing' | 'customer_pool_mismatch' | '';
      eligible: string[];
    }>();
    for (const file of this.spiritCandidateFiles) {
      for (const candidate of file.candidates) {
        const key = `${file.filename}\u0000${candidate.master_bol_number}`;
        if (candidate.selected) {
          previouslySelected.add(key);
        }
        if (candidate.pool_override) {
          previousPoolOverrides.set(key, candidate.pool_override);
        }
        if (candidate.allow_unmapped_stores) {
          previousAllowUnmapped.add(key);
        }
        previousPoolMetadata.set(key, {
          required: candidate.pool_override_required,
          reason: candidate.pool_override_reason,
          eligible: candidate.eligible_pool_overrides.slice()
        });
      }
    }

    this.spiritCandidateFiles = files.map((file) => {
      const groupedRows = new Map<string, Record<string, string>[]>();
      const allRowsByMasterBol = new Map<string, Record<string, string>[]>();
      const spencerRowsByMasterBol = new Map<string, number>();

      for (const row of file.rows) {
        const locationType = this.rawLocationType(row);
        const masterBol = (row['master_bol_number'] ?? '').trim();
        if (!masterBol) {
          continue;
        }

        const masterBolRows = allRowsByMasterBol.get(masterBol) ?? [];
        masterBolRows.push(row);
        allRowsByMasterBol.set(masterBol, masterBolRows);

        if (locationType === 'spencer') {
          spencerRowsByMasterBol.set(masterBol, (spencerRowsByMasterBol.get(masterBol) ?? 0) + 1);
          continue;
        }

        if (locationType !== 'spirit') {
          continue;
        }

        const rows = groupedRows.get(masterBol) ?? [];
        rows.push(row);
        groupedRows.set(masterBol, rows);
      }

      const candidates = Array.from(groupedRows.entries())
        .map(([masterBol, rows]): SpiritMasterBolCandidate => {
          const candidateKey = `${file.filename}\u0000${masterBol}`;
          const spencerRowCount = spencerRowsByMasterBol.get(masterBol) ?? 0;
          const required = spencerRowCount > 0;
          const sourcePoolPoints = this.uniqueFieldValues(rows, 'pool_point');
          const missingPoolRows = rows.filter((row) => !(row['pool_point'] ?? '').trim()).length;
          const sourcePool = sourcePoolPoints.length
            ? `${sourcePoolPoints.join(', ')}${missingPoolRows ? ` + ${missingPoolRows} blank` : ''}`
            : `Missing (${missingPoolRows} rows)`;
          const previousMetadata = previousPoolMetadata.get(candidateKey);
          const inferredEligibleOverrides = previousMetadata?.eligible
            ?? this.inferSpiritPoolOverrides(sourcePoolPoints, missingPoolRows);
          const inferredReason = previousMetadata?.reason
            ?? (missingPoolRows > 0
              ? 'missing'
              : (inferredEligibleOverrides.length ? 'customer_pool_mismatch' : ''));
          return {
            master_bol_number: masterBol,
            customer_kind: required ? 'mixed' : 'spirit',
            required,
            spencer_row_count: spencerRowCount,
            spirit_row_count: rows.length,
            mixed_pool_points: required
              ? this.uniqueFieldValues(allRowsByMasterBol.get(masterBol) ?? rows, 'pool_point')
              : sourcePoolPoints,
            source_pool: sourcePool,
            source_pool_points: sourcePoolPoints,
            missing_pool_rows: missingPoolRows,
            pool_override_required: previousMetadata?.required
              ?? (missingPoolRows > 0 || inferredEligibleOverrides.length > 0),
            pool_override_reason: inferredReason,
            eligible_pool_overrides: inferredEligibleOverrides,
            pool_override: previousPoolOverrides.get(candidateKey) ?? '',
            preview_effective_pool: '',
            allow_unmapped_stores: previousAllowUnmapped.has(candidateKey),
            preview_allow_unmapped_stores: false,
            warehouse: this.fieldValueSummary(rows, 'warehouse'),
            order_count: this.uniqueFieldValues(rows, 'order_number').length,
            carton_count: this.uniqueFieldValues(rows, 'carton_number').length,
            selected: required || previouslySelected.has(candidateKey)
          };
        })
        .sort((a, b) => a.master_bol_number.localeCompare(b.master_bol_number));

      return { filename: file.filename, candidates };
    });
  }

  private applyImmediateSpiritPoolEligibility(): void {
    for (const file of this.spiritCandidateFiles) {
      for (const candidate of file.candidates) {
        if (candidate.missing_pool_rows > 0 || candidate.pool_override_reason === 'customer_pool_mismatch') {
          continue;
        }

        const eligible = this.inferSpiritPoolOverrides(candidate.source_pool_points, candidate.missing_pool_rows);
        if (!eligible.length) {
          continue;
        }

        candidate.pool_override_required = true;
        candidate.pool_override_reason = 'customer_pool_mismatch';
        candidate.eligible_pool_overrides = eligible;
      }
    }
  }

  private inferSpiritPoolOverrides(sourcePoolPoints: string[], missingPoolRows: number): string[] {
    if (missingPoolRows > 0 || sourcePoolPoints.length !== 1) {
      return [];
    }

    const sourcePool = sourcePoolPoints[0];
    if (this.spiritPoolOptions.some((pool) => pool.pool_point_client_id === sourcePool)) {
      return [];
    }

    return this.normalizeStringArray(
      this.spiritPoolOptions
        .filter((pool) => this.normalizeStringArray(pool.same_pool_client_ids ?? []).includes(sourcePool))
        .map((pool) => pool.pool_point_client_id)
    );
  }

  private clearSpiritPoolAcknowledgements(): void {
    for (const file of this.spiritCandidateFiles) {
      for (const candidate of file.candidates) {
        candidate.preview_effective_pool = '';
        candidate.preview_allow_unmapped_stores = false;
      }
    }
  }

  private applySpiritCandidateMetadata(files: SpencerAsnFilePreview[]): void {
    const previewsByFilename = new Map(files.map((file): [string, SpencerAsnFilePreview] => [file.filename, file]));

    for (const file of this.spiritCandidateFiles) {
      const groups = previewsByFilename.get(file.filename)?.spirit_groups ?? [];
      const groupsByMasterBol = new Map(
        groups.map((group): [string, SpencerAsnSpiritGroupPreview] => [String(group.master_bol_number), group])
      );

      for (const candidate of file.candidates) {
        const group = groupsByMasterBol.get(candidate.master_bol_number);
        if (!group) {
          continue;
        }

        candidate.pool_override_required = group.pool_override_required === true;
        candidate.pool_override_reason = group.pool_override_reason === 'customer_pool_mismatch'
          ? 'customer_pool_mismatch'
          : (group.pool_override_reason === 'missing' ? 'missing' : '');
        candidate.eligible_pool_overrides = this.normalizeStringArray(group.eligible_pool_overrides ?? []);

        if (candidate.pool_override
          && candidate.eligible_pool_overrides.length
          && !candidate.eligible_pool_overrides.includes(candidate.pool_override)) {
          candidate.pool_override = '';
          candidate.allow_unmapped_stores = false;
        }
      }
    }
  }

  private applySpiritPoolAcknowledgements(files: SpencerAsnFilePreview[]): void {
    const previewsByFilename = new Map(files.map((file): [string, SpencerAsnFilePreview] => [file.filename, file]));

    for (const file of this.spiritCandidateFiles) {
      const acknowledgement = previewsByFilename.get(file.filename)?.spirit_selection;
      const effectiveOverrides = this.normalizePoolOverrideMap(acknowledgement?.effective_pool_overrides ?? {});
      const effectiveAllowUnmapped = this.normalizeStringArray(
        acknowledgement?.effective_allow_unmapped_spirit_stores ?? []
      );

      for (const candidate of file.candidates) {
        candidate.preview_effective_pool = candidate.selected
          ? (effectiveOverrides[candidate.master_bol_number] ?? '')
          : '';
        candidate.preview_allow_unmapped_stores = candidate.selected
          && effectiveAllowUnmapped.includes(candidate.master_bol_number);
      }
    }
  }

  private currentSpiritSelectionsByFilename(filenames: string[] = this.selectedFilenames()): SpiritSelectionsByFilename {
    const candidatesByFilename = new Map(
      this.spiritCandidateFiles.map((file): [string, SpiritCandidateFile] => [file.filename, file])
    );
    const selections: SpiritSelectionsByFilename = {};

    for (const filename of this.normalizeStringArray(filenames)) {
      const file = candidatesByFilename.get(filename);
      selections[filename] = this.normalizeStringArray(
        (file?.candidates ?? [])
          // Required mixed-load candidates stay in the request even if the
          // checkbox state is altered outside Angular's disabled control.
          .filter((candidate) => candidate.required || candidate.selected)
          .map((candidate) => candidate.master_bol_number)
      );
    }

    return selections;
  }

  private currentSpiritPoolOverridesByFilename(filenames: string[] = this.selectedFilenames()): SpiritPoolOverridesByFilename {
    const candidatesByFilename = new Map(
      this.spiritCandidateFiles.map((file): [string, SpiritCandidateFile] => [file.filename, file])
    );
    const overrides: SpiritPoolOverridesByFilename = {};

    for (const filename of this.normalizeStringArray(filenames)) {
      const file = candidatesByFilename.get(filename);
      const fileOverrides: Record<string, string> = {};
      for (const candidate of file?.candidates ?? []) {
        const poolCode = candidate.pool_override.trim();
        if ((candidate.required || candidate.selected) && this.spiritPoolPickerVisible(candidate) && poolCode) {
          fileOverrides[candidate.master_bol_number] = poolCode;
        }
      }
      overrides[filename] = this.normalizePoolOverrideMap(fileOverrides);
    }

    return overrides;
  }

  private currentSpiritAllowUnmappedByFilename(
    filenames: string[] = this.selectedFilenames()
  ): SpiritAllowUnmappedByFilename {
    const candidatesByFilename = new Map(
      this.spiritCandidateFiles.map((file): [string, SpiritCandidateFile] => [file.filename, file])
    );
    const acknowledgements: SpiritAllowUnmappedByFilename = {};

    for (const filename of this.normalizeStringArray(filenames)) {
      const file = candidatesByFilename.get(filename);
      acknowledgements[filename] = this.normalizeStringArray(
        (file?.candidates ?? [])
          .filter((candidate) => {
            return (candidate.required || candidate.selected)
              && this.isSpiritPoolCorrection(candidate)
              && candidate.allow_unmapped_stores;
          })
          .map((candidate) => candidate.master_bol_number)
      );
    }

    return acknowledgements;
  }

  private filesAcknowledgeSpiritSelection(
    files: Array<{ filename: string; spirit_selection?: SpencerAsnSpiritSelection }>,
    expectedByFilename: SpiritSelectionsByFilename,
    expectedPoolOverridesByFilename: SpiritPoolOverridesByFilename,
    expectedAllowUnmappedByFilename: SpiritAllowUnmappedByFilename
  ): boolean {
    const expectedFilenames = Object.keys(expectedByFilename).sort();
    const returnedFilenames = files.map((file) => file.filename).sort();

    if (!this.sameStringArrays(expectedFilenames, returnedFilenames)) {
      return false;
    }

    return files.every((file) => {
      return this.spiritSelectionAcknowledges(
        file.spirit_selection,
        expectedByFilename[file.filename] ?? [],
        expectedPoolOverridesByFilename[file.filename] ?? {},
        expectedAllowUnmappedByFilename[file.filename] ?? []
      );
    });
  }

  private spiritSelectionAcknowledges(
    acknowledgement: SpencerAsnSpiritSelection | undefined,
    expectedMasterBols: string[],
    expectedPoolOverrides: Record<string, string>,
    expectedAllowUnmapped: string[]
  ): boolean {
    if (!acknowledgement) {
      return false;
    }

    const expected = this.normalizeStringArray(expectedMasterBols);
    const requested = this.normalizeStringArray(acknowledgement.requested_master_bols ?? []);
    const effective = this.normalizeStringArray(acknowledgement.effective_master_bols ?? []);
    const selected = this.normalizeStringArray(acknowledgement.selected_master_bols ?? []);
    const available = this.normalizeStringArray(acknowledgement.available_master_bols ?? []);
    const unselected = this.normalizeStringArray(acknowledgement.unselected_master_bols ?? []);
    const expectedSet = new Set(expected);
    const calculatedUnselected = available.filter((masterBol) => !expectedSet.has(masterBol));
    const requestedPoolOverrides = this.normalizePoolOverrideMap(acknowledgement.requested_pool_overrides ?? {});
    const effectivePoolOverrides = this.normalizePoolOverrideMap(acknowledgement.effective_pool_overrides ?? {});
    const expectedOverrides = this.normalizePoolOverrideMap(expectedPoolOverrides);
    const requestedAllowUnmapped = this.normalizeStringArray(
      acknowledgement.requested_allow_unmapped_spirit_stores ?? []
    );
    const effectiveAllowUnmapped = this.normalizeStringArray(
      acknowledgement.effective_allow_unmapped_spirit_stores ?? []
    );
    const expectedAllow = this.normalizeStringArray(expectedAllowUnmapped);

    return this.sameStringArrays(requested, expected)
      && this.sameStringArrays(effective, expected)
      && this.sameStringArrays(selected, expected)
      && expected.every((masterBol) => available.includes(masterBol))
      && this.sameStringArrays(unselected, calculatedUnselected)
      && acknowledgement.selected_count === expected.length
      && acknowledgement.unselected_count === unselected.length
      && this.poolOverrideMapsEqual(requestedPoolOverrides, expectedOverrides)
      && this.poolOverrideMapsEqual(effectivePoolOverrides, expectedOverrides)
      && this.sameStringArrays(requestedAllowUnmapped, expectedAllow)
      && this.sameStringArrays(effectiveAllowUnmapped, expectedAllow)
      && !(acknowledgement.validation_errors ?? []).length;
  }

  private spiritSelectionMapsEqual(
    left: SpiritSelectionsByFilename,
    right: SpiritSelectionsByFilename
  ): boolean {
    const leftFilenames = Object.keys(left).sort();
    const rightFilenames = Object.keys(right).sort();

    return this.sameStringArrays(leftFilenames, rightFilenames)
      && leftFilenames.every((filename) => {
        return this.sameStringArrays(
          this.normalizeStringArray(left[filename] ?? []),
          this.normalizeStringArray(right[filename] ?? [])
        );
      });
  }

  private cloneSpiritSelections(selections: SpiritSelectionsByFilename): SpiritSelectionsByFilename {
    const clone: SpiritSelectionsByFilename = {};
    for (const filename of Object.keys(selections)) {
      clone[filename] = this.normalizeStringArray(selections[filename] ?? []);
    }
    return clone;
  }

  private spiritPoolOverrideMapsEqual(
    left: SpiritPoolOverridesByFilename,
    right: SpiritPoolOverridesByFilename
  ): boolean {
    const leftFilenames = Object.keys(left).sort();
    const rightFilenames = Object.keys(right).sort();
    return this.sameStringArrays(leftFilenames, rightFilenames)
      && leftFilenames.every((filename) => this.poolOverrideMapsEqual(
        this.normalizePoolOverrideMap(left[filename] ?? {}),
        this.normalizePoolOverrideMap(right[filename] ?? {})
      ));
  }

  private poolOverrideMapsEqual(left: Record<string, string>, right: Record<string, string>): boolean {
    const leftKeys = Object.keys(left).sort();
    const rightKeys = Object.keys(right).sort();
    return this.sameStringArrays(leftKeys, rightKeys)
      && leftKeys.every((key) => left[key] === right[key]);
  }

  private cloneSpiritPoolOverrides(overrides: SpiritPoolOverridesByFilename): SpiritPoolOverridesByFilename {
    const clone: SpiritPoolOverridesByFilename = {};
    for (const filename of Object.keys(overrides)) {
      clone[filename] = this.normalizePoolOverrideMap(overrides[filename] ?? {});
    }
    return clone;
  }

  private spiritAllowUnmappedMapsEqual(
    left: SpiritAllowUnmappedByFilename,
    right: SpiritAllowUnmappedByFilename
  ): boolean {
    const leftFilenames = Object.keys(left).sort();
    const rightFilenames = Object.keys(right).sort();
    return this.sameStringArrays(leftFilenames, rightFilenames)
      && leftFilenames.every((filename) => this.sameStringArrays(
        this.normalizeStringArray(left[filename] ?? []),
        this.normalizeStringArray(right[filename] ?? [])
      ));
  }

  private cloneSpiritAllowUnmapped(
    acknowledgements: SpiritAllowUnmappedByFilename
  ): SpiritAllowUnmappedByFilename {
    const clone: SpiritAllowUnmappedByFilename = {};
    for (const filename of Object.keys(acknowledgements)) {
      clone[filename] = this.normalizeStringArray(acknowledgements[filename] ?? []);
    }
    return clone;
  }

  private normalizePoolOverrideMap(value: unknown): Record<string, string> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }
    const normalized: Record<string, string> = {};
    for (const [masterBol, poolCode] of Object.entries(value as Record<string, unknown>)) {
      const key = String(masterBol ?? '').trim();
      const code = String(poolCode ?? '').trim();
      if (key && code) {
        normalized[key] = code;
      }
    }
    return Object.keys(normalized).sort().reduce((result, key) => {
      result[key] = normalized[key];
      return result;
    }, {} as Record<string, string>);
  }

  private normalizeStringArray(values: unknown): string[] {
    if (!Array.isArray(values)) {
      return [];
    }

    return Array.from(new Set(
      values
        .map((value) => String(value ?? '').trim())
        .filter((value) => value !== '')
    )).sort();
  }

  private sameStringArrays(left: string[], right: string[]): boolean {
    return left.length === right.length && left.every((value, index) => value === right[index]);
  }

  private uniqueFieldValues(rows: Record<string, string>[], field: string): string[] {
    return this.normalizeStringArray(rows.map((row) => row[field] ?? ''));
  }

  private fieldValueSummary(rows: Record<string, string>[], field: string): string {
    const values = this.uniqueFieldValues(rows, field);
    return values.length ? values.join(', ') : 'Missing';
  }

  private parseDownloadedFile(file: SpencerAsnDownloadedFile): SpencerAsnParsedFile {
    const lines = this.splitCsvLines(file.content).filter((line) => line.trim() !== '');

    if (!lines.length) {
      return {
        filename: file.filename,
        content: file.content,
        headers: [],
        rows: [],
        skipped_rows: 0,
        row_count: 0
      };
    }

    const headers = this.parseCsvLine(lines[0]).map((header) => header.trim());
    const rows: Record<string, string>[] = [];
    let skippedRows = 0;

    for (const line of lines.slice(1)) {
      const values = this.parseCsvLine(line).map((value) => value.trim());

      if (values.length < headers.length) {
        skippedRows++;
        continue;
      }

      const row: Record<string, string> = {};
      headers.forEach((header, index) => {
        row[header] = values[index] ?? '';
      });
      rows.push(row);
    }

    return {
      filename: file.filename,
      content: file.content,
      headers,
      rows,
      skipped_rows: skippedRows,
      row_count: rows.length
    };
  }

  private splitCsvLines(content: string): string[] {
    const lines: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let index = 0; index < content.length; index++) {
      const char = content[index];
      const next = content[index + 1];

      if (char === '"') {
        if (inQuotes && next === '"') {
          current += char + next;
          index++;
          continue;
        }

        inQuotes = !inQuotes;
      }

      if (!inQuotes && (char === '\n' || char === '\r')) {
        lines.push(current);
        current = '';

        if (char === '\r' && next === '\n') {
          index++;
        }

        continue;
      }

      current += char;
    }

    if (current !== '') {
      lines.push(current);
    }

    return lines;
  }

  private parseCsvLine(line: string): string[] {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let index = 0; index < line.length; index++) {
      const char = line[index];
      const next = line[index + 1];

      if (char === '"') {
        if (inQuotes && next === '"') {
          current += '"';
          index++;
          continue;
        }

        inQuotes = !inQuotes;
        continue;
      }

      if (char === ',' && !inQuotes) {
        values.push(current);
        current = '';
        continue;
      }

      current += char;
    }

    values.push(current);
    return values;
  }

  private rawMasterBolValue(row: Record<string, string>): string {
    return (row['master_bol_number'] ?? '').trim() || 'Missing Master BOL';
  }

  private rawLocationType(row: Record<string, string>): 'spencer' | 'spirit' | 'other' {
    const locationName = (row['location_name'] ?? '').trim().toUpperCase();

    if (locationName.includes('SPIRIT')) {
      return 'spirit';
    }

    if (locationName.includes('SPENCER')) {
      return 'spencer';
    }

    return 'other';
  }

  private uniqueMasterBols(rows: Record<string, string>[]): string[] {
    const values = rows
      .map((row) => (row['master_bol_number'] ?? '').trim())
      .filter((value) => value !== '');

    return Array.from(new Set(values));
  }

  private saveCsvFile(file: SpencerAsnDownloadedFile): void {
    const blob = new Blob([file.content], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = file.filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private processResultSucceeded(
    result: SpencerAsnProcessResult,
    expectedSpiritMasterBols: string[],
    expectedSpiritPoolOverrides: Record<string, string>,
    expectedAllowUnmapped: string[]
  ): boolean {
    return this.processResultStatusSucceeded(result)
      && this.spiritSelectionAcknowledges(
        result.spirit_selection,
        expectedSpiritMasterBols,
        expectedSpiritPoolOverrides,
        expectedAllowUnmapped
      );
  }

  private processResultStatusSucceeded(result: SpencerAsnProcessResult): boolean {
    if ((result.status || '').trim().toLowerCase() !== 'success') {
      return false;
    }

    return !(result.messages ?? []).some((message) => {
      return ['error', 'failed', 'blocked'].includes((message.status || '').trim().toLowerCase());
    });
  }

  private successfulProcessFilenames(
    response: SpencerAsnProcessResponse,
    requestedFilenames: string[],
    expectedSpiritSelections: SpiritSelectionsByFilename,
    expectedSpiritPoolOverrides: SpiritPoolOverridesByFilename,
    expectedAllowUnmapped: SpiritAllowUnmappedByFilename
  ): string[] {
    const requested = new Set(requestedFilenames);
    const successful = (response.results ?? [])
      .filter((result) => {
        return requested.has(result.filename)
          && this.processResultSucceeded(
            result,
            expectedSpiritSelections[result.filename] ?? [],
            expectedSpiritPoolOverrides[result.filename] ?? {},
            expectedAllowUnmapped[result.filename] ?? []
          );
      })
      .map((result) => result.filename);

    return Array.from(new Set(successful));
  }

  private processResponseSucceeded(
    response: SpencerAsnProcessResponse,
    requestedFilenames: string[],
    expectedSpiritSelections: SpiritSelectionsByFilename,
    expectedSpiritPoolOverrides: SpiritPoolOverridesByFilename,
    expectedAllowUnmapped: SpiritAllowUnmappedByFilename
  ): boolean {
    const results = response.results ?? [];
    const requested = new Set(requestedFilenames);
    const returned = new Set(results.map((result) => result.filename));

    return response.success === true
      && results.length === requested.size
      && returned.size === requested.size
      && results.every((result) => {
        return requested.has(result.filename)
          && this.processResultSucceeded(
            result,
            expectedSpiritSelections[result.filename] ?? [],
            expectedSpiritPoolOverrides[result.filename] ?? {},
            expectedAllowUnmapped[result.filename] ?? []
          );
      });
  }

  private processFailureMessage(
    response: SpencerAsnProcessResponse,
    requestedFilenames: string[],
    expectedSpiritSelections: SpiritSelectionsByFilename,
    expectedSpiritPoolOverrides: SpiritPoolOverridesByFilename,
    expectedAllowUnmapped: SpiritAllowUnmappedByFilename
  ): string {
    const resultByFilename = new Map<string, SpencerAsnProcessResult>(
      (response.results ?? []).map((result): [string, SpencerAsnProcessResult] => [result.filename, result])
    );
    const acknowledgementFailures = requestedFilenames.filter((filename) => {
      const result = resultByFilename.get(filename);
      return !!result
        && this.processResultStatusSucceeded(result)
        && !this.spiritSelectionAcknowledges(
          result.spirit_selection,
          expectedSpiritSelections[filename] ?? [],
          expectedSpiritPoolOverrides[filename] ?? {},
          expectedAllowUnmapped[filename] ?? []
        );
    });

    if (acknowledgementFailures.length) {
      return `The process response did not acknowledge the exact Spirit Master BOL selection, pool choices, and held/out-of-market carton choices for ${acknowledgementFailures.join(', ')}. Those files were not archived. Review Results.`;
    }

    const failedFilenames = requestedFilenames.filter((filename) => {
      const result = resultByFilename.get(filename);
      return !result || !this.processResultSucceeded(
        result,
        expectedSpiritSelections[filename] ?? [],
        expectedSpiritPoolOverrides[filename] ?? {},
        expectedAllowUnmapped[filename] ?? []
      );
    });

    if (failedFilenames.length) {
      return `Processing failed or was blocked for ${failedFilenames.length} selected file${failedFilenames.length === 1 ? '' : 's'}: ${failedFilenames.join(', ')}. Review Results.`;
    }

    return `${response.message || 'The process request returned an unsuccessful status.'} Review Results.`;
  }

  private archiveFilesFromProcess(
    response: SpencerAsnProcessResponse,
    requestedFilenames: string[],
    expectedSpiritSelections: SpiritSelectionsByFilename,
    expectedSpiritPoolOverrides: SpiritPoolOverridesByFilename,
    expectedAllowUnmapped: SpiritAllowUnmappedByFilename
  ): string[] {
    const successful = new Set(
      this.successfulProcessFilenames(
        response,
        requestedFilenames,
        expectedSpiritSelections,
        expectedSpiritPoolOverrides,
        expectedAllowUnmapped
      )
    );

    if (Array.isArray(response.archive_files)) {
      return Array.from(new Set(response.archive_files.filter((filename) => successful.has(filename))));
    }

    return this.processResponseSucceeded(
      response,
      requestedFilenames,
      expectedSpiritSelections,
      expectedSpiritPoolOverrides,
      expectedAllowUnmapped
    )
      ? Array.from(successful)
      : [];
  }

  private archiveProcessedFiles(
    filenames: string[],
    processComplete: boolean,
    requestedFileCount: number,
    processFailureMessage: string
  ): void {
    this.http.post<SpencerAsnArchiveResponse>(this.apiUrl, {
      action: 'archive',
      filenames
    }).subscribe({
      next: (response) => {
        this.processing = false;

        if (response.success && processComplete && filenames.length === requestedFileCount) {
          this.toastr.success('Selected ASN files processed and archived.');
        } else if (response.success && processComplete) {
          this.toastr.warning(`All selected ASN files were processed; ${filenames.length} confirmed file${filenames.length === 1 ? '' : 's'} archived.`);
        } else if (response.success) {
          this.toastr.warning(`${filenames.length} confirmed successful file${filenames.length === 1 ? '' : 's'} archived. ${processFailureMessage}`);
        } else if (processComplete) {
          this.toastr.warning('Processed, but one or more confirmed files did not archive.');
        } else {
          this.toastr.error(`Processing was partial, and one or more confirmed-success files did not archive. ${processFailureMessage}`);
        }
        this.loadFiles();
      },
      error: (error) => {
        const archiveError = this.errorText(error, 'Unable to archive confirmed-success files.');
        this.errorMessage = processComplete ? `Processed, but ${archiveError}` : `${archiveError} ${processFailureMessage}`;
        this.toastr.error(this.errorMessage);
        this.processing = false;
        this.loadFiles();
      }
    });
  }

  private selectedFilenames(): string[] {
    return this.visibleFiles.filter((file) => file.selected).map((file) => file.filename);
  }

  private initializeTheme(): void {
    const storedTheme = window.localStorage.getItem(this.themeStorageKey);
    if (storedTheme === 'dark' || storedTheme === 'light') {
      this.themeMode = storedTheme;
      return;
    }

    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    this.themeMode = prefersDark ? 'dark' : 'light';
  }

  private errorText(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse) {
      return error.error?.message || error.message || fallback;
    }

    return fallback;
  }
}
